import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productAPI, wishlistAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { ArrowLeft, Star } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ProductPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);

  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => productAPI.getOne(slug).then((res) => res.data),
  });

  const { data: wishlist = [] } = useQuery({
    queryKey: ['wishlist'],
    enabled: !!user,
    queryFn: () => wishlistAPI.get().then((res) => res.data),
  });

  const addFavorite = useMutation({
    mutationFn: (productId) => wishlistAPI.add(productId),
    onSuccess: () => {
      toast.success('Produkt u shtua në favorites');
      queryClient.invalidateQueries(['wishlist']);
    },
    onError: () => toast.error('Gabim gjatë shtimit në favorites'),
  });

  const removeFavorite = useMutation({
    mutationFn: (productId) => wishlistAPI.remove(productId),
    onSuccess: () => {
      toast.success('Produkt u hoq nga favorites');
      queryClient.invalidateQueries(['wishlist']);
    },
    onError: () => toast.error('Gabim gjatë heqjes nga favorites'),
  });

  if (isLoading || authLoading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (isError || !data) return (
    <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
      <p style={{ color: 'var(--text-light)', marginBottom: 16 }}>Ndodhi një gabim gjatë ngarkimit të produktit.</p>
      <Link to="/shop" className="btn btn-primary">Kthehu te Dyqani</Link>
    </div>
  );

  const product = data;
  const images = (() => { try { return JSON.parse(product.images || '[]'); } catch { return [product.images]; } })();
  const price = Number(product.price) || 0;
  const comparePrice = Number(product.compare_price) || 0;
  const rating = Number(product.avg_rating) || 0;
  const stock = Number(product.stock) || 0;

  return (
    <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80 }}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/shop" className="btn btn-ghost"><ArrowLeft size={16} /> Kthehu te Dyqani</Link>
      </div>

      <div className="product-page-grid">
        <div className="product-gallery">
          <div className="product-image-main">
            <img src={images[0] || 'https://via.placeholder.com/520x520?text=Produkt'} alt={product.name} />
          </div>
          <div className="product-image-thumbs">
            {images.map((src, index) => (
              <img key={index} src={src} alt={`${product.name} ${index + 1}`} />
            ))}
          </div>
        </div>

        <div className="product-details">
          <div className="product-meta-row">
            <div className="product-meta">
              <span className="badge badge-gold">{product.category_name || 'Pa kategori'}</span>
              {product.is_featured && <span className="badge badge-green" style={{ marginLeft: 8 }}>Bestseller</span>}
            </div>
            <button
              type="button"
              className={`btn btn-ghost btn-sm${wishlist.some((item) => item.product_id === product.id) ? ' active' : ''}`}
              onClick={() => {
                if (!user) {
                  toast.error('Duhet të jeni të kyçur për të shtuar në favorites');
                  return;
                }
                const favoriteItem = wishlist.find((item) => item.product_id === product.id);
                if (favoriteItem) {
                  removeFavorite.mutate(product.id);
                } else {
                  addFavorite.mutate(product.id);
                }
              }}
            >
              {wishlist.some((item) => item.product_id === product.id) ? 'Hiq nga favorites' : 'Shto në favorites'}
            </button>
          </div>
          <h1 style={{ fontSize: '2.4rem', margin: '18px 0 10px' }}>{product.name}</h1>
          <div className="product-rating">
            <span>{'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}</span>
            <span style={{ color: 'var(--text-light)', marginLeft: 12 }}>{product.review_count || 0} recensione</span>
          </div>
          <div className="product-price-row">
            <div>
              <div className="product-price-main">${price.toFixed(2)}</div>
              {comparePrice > 0 && <div className="product-price-old">${comparePrice.toFixed(2)}</div>}
            </div>
            <div className="product-stock">{stock > 0 ? `${stock} në stok` : 'Jashtë stoku'}</div>
          </div>
          <p style={{ color: 'var(--text-light)', lineHeight: 1.8, marginBottom: 24 }}>{product.short_desc || product.description || 'Pa përshkrim të disponueshëm.'}</p>

          <div className="product-buy-panel">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ color: 'var(--text-light)' }}>Sasia</label>
              <input
                type="number"
                min="1"
                max={Math.max(1, stock)}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                className="form-input"
                style={{ width: 90 }}
              />
            </div>
            <button
              className="btn btn-primary btn-lg"
              onClick={() => addItem(product.id, quantity)}
              disabled={stock === 0}
            >
              {stock === 0 ? 'Pa stok' : 'Shto në shportë'}
            </button>
          </div>

          <div className="product-info-grid">
            <div>
              <div className="section-eyebrow">Detajet e produktit</div>
              <p>{product.description || 'Nuk ka informacione shtesë.'}</p>
            </div>
            <div>
              <div className="section-eyebrow">Përbërësit</div>
              <p>{product.ingredients || 'Nuk është dhënë.'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="product-reviews">
        <h2 className="section-title" style={{ marginBottom: 18 }}>Vlerësimet</h2>
        {product.reviews?.length > 0 ? (
          product.reviews.map((review) => (
            <div key={review.id} className="review-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <strong>{review.user_name || 'Përdorues'}</strong>
                  <div style={{ color: 'var(--text-light)', fontSize: '.85rem' }}>{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</div>
                </div>
                <span style={{ color: 'var(--text-light)', fontSize: '.85rem' }}>{new Date(review.created_at).toLocaleDateString('sq')}</span>
              </div>
              {review.title && <h3 style={{ margin: '12px 0 8px' }}>{review.title}</h3>}
              <p style={{ lineHeight: 1.8, color: 'var(--text-mid)' }}>{review.body}</p>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <h3>Nuk ka ende vlerësime</h3>
            <p>Jini i pari që shton një koment për këtë produkt.</p>
          </div>
        )}
      </div>

      <style>{`
        .product-page-grid { display: grid; grid-template-columns: 1.2fr .8fr; gap: 36px; }
        .product-gallery { display: flex; flex-direction: column; gap: 16px; }
        .product-image-main { background: var(--cream); border-radius: var(--radius-xl); padding: 24px; display: flex; align-items: center; justify-content: center; min-height: 420px; }
        .product-image-main img { max-width: 100%; max-height: 100%; object-fit: contain; }
        .product-image-thumbs { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
        .product-image-thumbs img { width: 100%; height: 80px; object-fit: cover; border-radius: var(--radius-md); border: 1px solid var(--cream-dark); }
        .product-details { display: flex; flex-direction: column; gap: 18px; }
        .product-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .product-rating { display: flex; align-items: center; gap: 12px; color: var(--gold); font-size: .95rem; }
        .product-price-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
        .product-price-main { font-family: 'Playfair Display', serif; font-size: 2.2rem; font-weight: 700; }
        .product-price-old { color: var(--text-light); text-decoration: line-through; margin-top: 6px; }
        .product-stock { color: ${stock > 0 ? 'var(--success)' : 'var(--danger)'}; font-weight: 700; }
        .product-buy-panel { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
        .product-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; padding: 24px 0 0; border-top: 1px solid var(--cream-dark); }
        .review-card { background: white; border-radius: var(--radius-xl); border: 1px solid var(--cream-dark); padding: 22px; margin-bottom: 18px; }
        @media(max-width: 960px) { .product-page-grid { grid-template-columns: 1fr; } .product-image-thumbs { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media(max-width: 680px) { .product-image-thumbs { grid-template-columns: repeat(2, minmax(0, 1fr)); } .product-info-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
